import axios from 'axios';
import { Message } from 'element-ui';
import router from '../router';
import store from '../store';
import { API_BASE_URL } from './base';

let redirectingToLogin = false;

function isLoginRoute() {
  try {
    if (router && router.currentRoute && router.currentRoute.path === '/login') return true;
  } catch (e) {}
  // Router may not have updated currentRoute yet (during guards); hash is the source of truth in hash-mode.
  try {
    const h = window && window.location ? window.location.hash || '' : '';
    return h === '#/login' || h.startsWith('#/login?') || h.startsWith('#/login/');
  } catch (e) {}
  return false;
}

function redirectToLoginOnce() {
  if (redirectingToLogin) return;
  if (isLoginRoute()) return;
  redirectingToLogin = true;
  // Vue Router 2 returns a promise if callbacks are omitted.
  Promise.resolve(router.replace('/login'))
    .catch(() => {})
    .finally(() => {
      redirectingToLogin = false;
    });
}

const http = axios.create({
  baseURL: API_BASE_URL,
  // Cookie-based auth (HttpOnly JWT) + CSRF token cookie.
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  // Axios 1.x only adds XSRF header for same-origin unless this is enabled.
  withXSRFToken: true
});

function readCookie(name) {
  try {
    const esc = String(name || '').replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    const m = document.cookie.match(new RegExp('(?:^|; )' + esc + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  } catch (e) {
    return '';
  }
}

http.interceptors.request.use((cfg) => {
  const token = readCookie('XSRF-TOKEN');
  if (token) {
    const headers = cfg && cfg.headers ? cfg.headers : {};
    const hasHeader =
      (typeof headers.get === 'function' && headers.get('X-XSRF-TOKEN')) ||
      headers['X-XSRF-TOKEN'];
    if (!hasHeader) {
      if (typeof headers.set === 'function') headers.set('X-XSRF-TOKEN', token);
      else headers['X-XSRF-TOKEN'] = token;
    }
    cfg.headers = headers;
  }
  return cfg;
});

http.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    const status = err && err.response ? err.response.status : 0;
    let data = err && err.response ? err.response.data : null;
    // axios may treat non-JSON error payloads as plain strings; try to parse common ApiError JSON.
    if (typeof data === 'string' && data.trim().startsWith('{')) {
      try {
        data = JSON.parse(data);
      } catch (e) {
        // keep as string
      }
    }
    const msg = (data && data.message) || (typeof data === 'string' ? data : null) || (err && err.message) || 'Request failed';

    // CSRF self-heal:
    // When the browser has TS_AUTH but lost the session-scoped XSRF-TOKEN cookie (common after browser restart),
    // Spring will reject the first POST with 403 and issue a new XSRF-TOKEN cookie.
    // We automatically seed CSRF and retry once to avoid "first click fails" UX.
    if (status === 403) {
      const cfg = err && err.config ? err.config : null;
      const method = cfg && cfg.method ? String(cfg.method).toLowerCase() : '';
      const isWrite = method && method !== 'get' && method !== 'head' && method !== 'options';
      const alreadyRetried = !!(cfg && cfg.__csrfRetry);
      const apiCode = data && typeof data === 'object' ? data.code : null;
      // Retry CSRF once for "likely CSRF" 403s.
      // If this was a true permission 403, the retry will fail again and surface normally.
      // If this was a business 403 (e.g. ACCOUNT_DISABLED), do not retry (avoid double-submitting one-time actions like captcha).
      const maybeCsrf = !apiCode || apiCode === 'FORBIDDEN' || apiCode === 'CSRF';

      if (cfg && isWrite && !alreadyRetried && maybeCsrf) {
        try {
          // GET endpoint will create/refresh XSRF-TOKEN cookie.
          await http.get('/api/auth/csrf', { __csrfRetry: true });
        } catch (e) {
          // ignore; fall through to error handling
        }
        cfg.__csrfRetry = true;
        return http(cfg);
      }
    }

    if (status === 401) {
      const cfg = err && err.config ? err.config : null;
      const url = cfg && cfg.url ? String(cfg.url) : '';
      const method = cfg && cfg.method ? String(cfg.method).toLowerCase() : '';
      const isWrite = method && method !== 'get' && method !== 'head' && method !== 'options';
      const alreadyRetried = !!(cfg && cfg.__csrfRetry);
      const isAuthCall = url && url.indexOf('/api/auth/') !== -1;
      const apiCode = data && typeof data === 'object' ? data.code : null;

      // Some CSRF failures can be translated to 401 when the request is treated as anonymous (filter order / first load).
      // Retry once after seeding CSRF, then fall back to normal 401 handling.
      if (cfg && isWrite && !alreadyRetried && !isAuthCall) {
        try {
          await http.get('/api/auth/csrf', { __csrfRetry: true });
        } catch (e) {}
        cfg.__csrfRetry = true;
        return http(cfg);
      }

      // Auth endpoints can legitimately return 401 (e.g. LOGIN_FAILED); do not force redirect loops.
      if (isAuthCall && apiCode) {
        Message.error(msg);
        return Promise.reject(err);
      }

      // Session expired / unauthenticated: clear local state; no need to hit /api/auth/logout again.
      // Guard: skip if already logged out to avoid cascading 401 loops.
      if (store.state.user) {
        store.dispatch('logout', { remote: false });
        redirectToLoginOnce();
      }
      return Promise.reject(err);
    }

    if (status === 423) {
      const announcementId = data && data.details ? data.details.announcementId : null;
      if (router.currentRoute && router.currentRoute.path !== '/announcements/confirm') {
        store.commit('setAnnouncementReturnTo', router.currentRoute.fullPath);
      }
      store.commit('setAnnouncementGate', {
        mustConfirmAnnouncement: true,
        unconfirmedAnnouncementId: announcementId
      });
      if (router.currentRoute.path !== '/announcements/confirm') {
        router.replace('/announcements/confirm');
      }
      return Promise.reject(err);
    }

    if (status) {
      Message.error(msg);
    }
    return Promise.reject(err);
  }
);

export default http;
